// React
import React, { useState, useEffect } from 'react';

// Prop types
import PropTypes from 'prop-types';

// Component
import classnames from 'classnames';

// Const
import { RECIPROCAL, MEMO_TODO_CLOSE, MEMO_TODO_OPEN } from '../../../const';

// Redux
import { connect } from 'react-redux';
import { withRoomContext } from '../../../RoomContext';

// Mui styles
import { withStyles, MuiThemeProvider, createTheme } from '@material-ui/core/styles';

// Mui core
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

// Mui icon
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import FlagIcon from '@material-ui/icons/Flag';

// Firebase
import { firestore } from '../../../lib/firebase';

const styles = () =>
	({
		activeIcon : {
			color : '#c0d748'
		},
		inactiveIcon : {
			color : '#adadac'
		},
		scrollBar : {
			overflow               : 'auto',
			height                 : 'inherit',
			background             : 'white',
			'&::-webkit-scrollbar' : {
				background   : '#ccc',
				width        : '7px',
				height       : '7px',
				borderRadius : '100px'
			},
			'&::-webkit-scrollbar-thumb' : {
				background   : '#888',
				borderRadius : '100px',
				'&:hover'    : {
					background : '#555'
				}
			}
		},
		row : {
			width         : '97%',
			marginBottom  : '0.8rem',
			display       : 'flex',
			flexDirection : 'row'
		},
		nonreciprocal : {
			background : '#878686'
		},
		reciprocal : {
			background : 'white'
		},
		rowIcon : {
			width  : '2rem',
			height : '2rem'
		},
		rowIconBg : {
			padding    : '0 0.7rem',
			display    : 'flex',
			alignItems : 'center'
		},
		rowContent : {
			width         : 'calc(100% - 2.7rem)',
			display       : 'flex',
			flexDirection : 'column'
		},
		rowInfo : {
			width          : '95%',
			display        : 'flex',
			justifyContent : 'space-between',
			borderBottom   : '1px solid white',
			marginTop      : '0.8rem'
		},
		rowReciprocalInfo : {
			borderBottom : '1px solid #878686'
		},
		rowDetail : {
			width         : '95%',
			display       : 'flex',
			flexDirection : 'row',
			color         : 'white',
			marginBottom  : '0.8rem',
			marginTop     : '0.5rem'
		},
		rowInput : {
			background   : '#999898',
			padding      : '0.2rem 0.5rem',
			borderRadius : '0.3rem',
			color        : 'white',
			textAlign    : 'left',
			border       : 'none',
			width        : 'inherit',
			marginRight  : '1rem',
			'&:focus'    : {
				outline : 'none'
			}
		},
		actionBtn : {
			background   : 'transparent',
			border       : '1px solid white',
			borderRadius : '0.3rem',
			color        : 'white',
			width        : '4rem',
			padding      : '0.1rem',
			fontSize     : '0.7rem',
			'&:hover'    :
			{
				backgroundColor : '#c0d748',
				color           : 'white',
				opacity         : 0.8
			}
		},
		nonreciprocalText : {
			color : 'white'
		},
		reciprocalText : {
			color : '#878686'
		},
		nonreciprocalBorder : {
			borderRadius : '0.3rem',
			border       : '1px solid white'
		},
		reciprocalBorder : {
			borderRadius : '0.3rem',
			border       : '1px solid #878686'
		}
	});

const theme = createTheme({
	overrides : {
		MuiButton : {
			startIcon : {
				marginRight : '0'
			}
		}
	}
});

const ToDoTable = ({
	classes,
	todoType,
	memos,
	onSelect
}) =>
{
	const isReciprocal = todoType === RECIPROCAL;
	const [ todoList, setTodoList ] = useState([]);

	const getDateString = (timestamp) =>
	{
		const date = timestamp ? new Date(timestamp) : new Date();
		const month = (`0${date.getMonth() + 1}`).slice(-2);
		const day = (`0${date.getDate()}`).slice(-2);
		const hour = (`0${date.getHours()}`).slice(-2);
		const min = (`0${date.getMinutes()}`).slice(-2);

		return `${date.getFullYear()}/${month}/${day} ${hour}:${min}`;
	};

	const onFlagChecked = (memo) =>
	{
		if (memo)
		{
			firestore.collection('memos').doc(memo.id)
				.update({
					status : memo.flag ? MEMO_TODO_OPEN : MEMO_TODO_CLOSE
				});
		}
	};

	useEffect(() =>
	{
		if (isReciprocal)
		{
			const nonreciprocals = [];

			memos.forEach((memo) =>
			{
				if (memo.status === MEMO_TODO_OPEN)
				{
					nonreciprocals.push({
						id        : memo.id,
						timestamp : memo.timestamp,
						name      : memo.name,
						user      : memo.user,
						sms       : memo.sms,
						title     : memo.title,
						message   : memo.message,
						comment   : memo.comment,
						flag      : false
					});
				}
			});

			setTodoList(nonreciprocals);
		}
		else
		{
			const reciprocals = [];

			memos.forEach((memo) =>
			{
				if (memo.status === MEMO_TODO_CLOSE)
				{
					reciprocals.push({
						id        : memo.id,
						timestamp : memo.timestamp,
						name      : memo.name,
						user      : memo.user,
						sms       : memo.sms,
						title     : memo.title,
						message   : memo.message,
						comment   : memo.comment,
						flag      : true
					});
				}
			});

			setTodoList(reciprocals);
		}
	}, [ isReciprocal, memos ]);

	return (
		<MuiThemeProvider theme={theme}>
			<Box className={classes.scrollBar}>
				{todoList.map((item) => (
					<Box
						key={item.id}
						className={classnames(
							classes.row,
							isReciprocal ? classes.nonreciprocal : classes.reciprocal,
							isReciprocal ? classes.nonreciprocalBorder : classes.reciprocalBorder
						)}
					>
						<Box className={classes.rowIconBg}>
							<FlagIcon
								className={classnames(
									classes.rowIcon,
									item.flag ? classes.activeIcon : classes.inactiveIcon
								)}
								onClick={() => onFlagChecked(item)}
							/>
						</Box>
						<Box
							className={classnames(
								classes.rowContent,
								isReciprocal ? classes.nonreciprocalText : classes.reciprocalText
							)}
						>
							<Box
								className={classnames(
									classes.rowInfo,
									isReciprocal ? null : classes.rowReciprocalInfo
								)}
							>
								<Typography>{getDateString(item.timestamp)}</Typography>
								<Typography>{item.name}</Typography>
							</Box>
							<Box className={classes.rowDetail}>
								<input
									defaultValue={item.title}
									className={classes.rowInput}
									type='text'
								/>
								<Button
									className={classnames(
										classes.actionBtn,
										isReciprocal ? null : classes.reciprocalText,
										isReciprocal ? classes.nonreciprocalBorder : classes.reciprocalBorder
									)}
									startIcon={<PlayArrowIcon/>}
									onClick={() => onSelect(item)}
								>
									詳細
								</Button>
							</Box>
						</Box>
					</Box>
				))}
			</Box>
		</MuiThemeProvider>
	);

};

ToDoTable.propTypes =
{
	classes  : PropTypes.object.isRequired,
	todoType : PropTypes.string.isRequired,
	memos    : PropTypes.array.isRequired,
	onSelect : PropTypes.func.isRequired
};

const mapStateToProps = (state) =>
{
	return {
		memos : state.room.memos
	};
};

const mapDispatchToProps = () =>
{
	return {

	};
};

export default withRoomContext(connect(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.room.memos === next.room.memos
			);
		}
	}
)(withStyles(styles)(ToDoTable)));
